import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useAcoes5W2H } from '@/hooks/useAcoes5W2H';
import { useUsers } from '@/hooks/useUsers';
import { PRIORIDADE_ACAO_LABELS, STATUS_ACAO_LABELS } from '@/types/plano-acao-extended';

const acaoSchema = z.object({
  what_oque: z.string().min(1, 'O que fazer é obrigatório'),
  why_porque: z.string().min(1, 'Por que fazer é obrigatório'),
  who_quem: z.string().optional(),
  where_onde: z.string().optional(),
  when_quando_inicio: z.string().optional(),
  when_quando_fim: z.string().optional(),
  how_como: z.string().optional(),
  how_much_quanto: z.coerce.number().optional(),
  custo_real: z.coerce.number().optional(),
  prioridade: z.string().optional(),
  status: z.string(),
  percentual_conclusao: z.number().min(0).max(100),
  motivo_bloqueio: z.string().optional(),
  resultado_obtido: z.string().optional(),
});

type AcaoFormData = z.infer<typeof acaoSchema>;

interface EditAcaoDialogProps {
  planoAcaoId: string;
  acao: {
    id: string;
    what_oque: string;
    why_porque: string;
    who_quem?: string | null;
    where_onde?: string | null;
    when_quando_inicio?: string | null;
    when_quando_fim?: string | null;
    how_como?: string | null;
    how_much_quanto?: number | null;
    custo_real?: number | null;
    status: string;
    prioridade?: string | null;
    percentual_conclusao?: number | null;
    motivo_bloqueio?: string | null;
    resultado_obtido?: string | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly?: boolean;
}

export const EditAcaoDialog = ({ planoAcaoId, acao, open, onOpenChange, readOnly }: EditAcaoDialogProps) => {
  const { updateAcao, isUpdating } = useAcoes5W2H(planoAcaoId);
  const { users } = useUsers();

  const form = useForm<AcaoFormData>({
    resolver: zodResolver(acaoSchema),
    defaultValues: {
      what_oque: '',
      why_porque: '',
      who_quem: '',
      where_onde: '',
      when_quando_inicio: '',
      when_quando_fim: '',
      how_como: '',
      how_much_quanto: undefined,
      custo_real: undefined,
      prioridade: 'media',
      status: 'pendente',
      percentual_conclusao: 0,
      motivo_bloqueio: '',
      resultado_obtido: '',
    },
  });

  useEffect(() => {
    if (acao) {
      form.reset({
        what_oque: acao.what_oque,
        why_porque: acao.why_porque,
        who_quem: acao.who_quem || '',
        where_onde: acao.where_onde || '',
        when_quando_inicio: acao.when_quando_inicio?.split('T')[0] || '',
        when_quando_fim: acao.when_quando_fim?.split('T')[0] || '',
        how_como: acao.how_como || '',
        how_much_quanto: acao.how_much_quanto || undefined,
        custo_real: acao.custo_real || undefined,
        prioridade: acao.prioridade || 'media',
        status: acao.status || 'pendente',
        percentual_conclusao: acao.percentual_conclusao || 0,
        motivo_bloqueio: acao.motivo_bloqueio || '',
        resultado_obtido: acao.resultado_obtido || '',
      });
    }
  }, [acao, form]);

  const currentStatus = form.watch('status');

  const onSubmit = (data: AcaoFormData) => {
    if (!acao) return;

    updateAcao({
      id: acao.id,
      what_oque: data.what_oque,
      why_porque: data.why_porque,
      who_quem: data.who_quem || null,
      where_onde: data.where_onde || null,
      when_quando_inicio: data.when_quando_inicio || null,
      when_quando_fim: data.when_quando_fim || null,
      how_como: data.how_como || null,
      how_much_quanto: data.how_much_quanto || null,
      custo_real: data.custo_real || null,
      prioridade: data.prioridade || null,
      status: data.status,
      percentual_conclusao: data.percentual_conclusao,
      motivo_bloqueio: data.motivo_bloqueio || null,
      resultado_obtido: data.resultado_obtido || null,
    }, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  if (!acao) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Ação 5W2H</DialogTitle>
          <DialogDescription>
            Atualize os detalhes e progresso da ação
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Status e Progresso */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(STATUS_ACAO_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="percentual_conclusao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Progresso: {field.value}%</FormLabel>
                    <FormControl>
                      <Slider
                        value={[field.value]}
                        onValueChange={(v) => field.onChange(v[0])}
                        max={100}
                        step={5}
                        disabled={readOnly}
                        className="mt-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Motivo Bloqueio (condicional) */}
            {currentStatus === 'bloqueada' && (
              <FormField
                control={form.control}
                name="motivo_bloqueio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-orange-600">Motivo do Bloqueio</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva o que está impedindo a execução" 
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* WHAT - O que fazer */}
            <FormField
              control={form.control}
              name="what_oque"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">WHAT</span>
                    O que fazer? *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Descreva a ação" {...field} disabled={readOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* WHY - Por que fazer */}
            <FormField
              control={form.control}
              name="why_porque"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">WHY</span>
                    Por que fazer? *
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Justificativa" {...field} disabled={readOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* WHO - Quem */}
              <FormField
                control={form.control}
                name="who_quem"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHO</span>
                      Quem vai fazer?
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* WHERE - Onde */}
              <FormField
                control={form.control}
                name="where_onde"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHERE</span>
                      Onde será feito?
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Local ou área" {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* WHEN - Início */}
              <FormField
                control={form.control}
                name="when_quando_inicio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">WHEN</span>
                      Data de início
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* WHEN - Fim */}
              <FormField
                control={form.control}
                name="when_quando_fim"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de conclusão</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* HOW - Como */}
            <FormField
              control={form.control}
              name="how_como"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">HOW</span>
                    Como será feito?
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Método ou passos" {...field} disabled={readOnly} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              {/* HOW MUCH - Custo Estimado */}
              <FormField
                control={form.control}
                name="how_much_quanto"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <span className="bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded">HOW MUCH</span>
                      Custo Estimado
                    </FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="R$" {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Custo Real */}
              <FormField
                control={form.control}
                name="custo_real"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Custo Real</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" placeholder="R$" {...field} disabled={readOnly} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Prioridade */}
              <FormField
                control={form.control}
                name="prioridade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridade</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PRIORIDADE_ACAO_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Resultado Obtido (para ações concluídas) */}
            {(currentStatus === 'concluida' || currentStatus === 'cancelada') && (
              <FormField
                control={form.control}
                name="resultado_obtido"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-green-600">Resultado Obtido</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva os resultados alcançados com esta ação" 
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={handleClose}>
                {readOnly ? 'Fechar' : 'Cancelar'}
              </Button>
              {!readOnly && (
                <Button type="submit" disabled={isUpdating}>
                  {isUpdating ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAcaoDialog;
